// components/Admin/CreateUserModal.tsx
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_USER_BY_ADMIN } from '../../graphql/mutations';
import { 
  Modal,
  TextField,
  PrimaryButton,
  DefaultButton,
  Stack,
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType
} from '@fluentui/react';

interface CreateUserModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  currentUserRole: string;
  refetchUsers: () => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ 
  isOpen, 
  onDismiss, 
  currentUserRole,
  refetchUsers
}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    company: '',
    role: 'user'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [createUser] = useMutation(CREATE_USER_BY_ADMIN, {
    onCompleted: () => {
      setSuccess(true);
      refetchUsers();
      setTimeout(() => {
        setSuccess(false);
        onDismiss();
        setFormData({
          username: '',
          email: '',
          phone: '',
          password: '',
          company: '',
          role: 'user'
        });
      }, 2000);
    },
    onError: (err) => {
      setError(err.message);
    }
  });

    // ✅ Форматирование телефона при вводе (как в AuthModal)
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    if (digits.length === 1) return `+${digits}`;

    let normalizedDigits = digits;
    if (digits.startsWith('8')) {
      normalizedDigits = '7' + digits.slice(1);
    } else if (!digits.startsWith('7')) {
      normalizedDigits = '7' + digits;
    }

    const part1 = normalizedDigits.slice(1, 4);
    const part2 = normalizedDigits.slice(4, 7);
    const part3 = normalizedDigits.slice(7, 9);
    const part4 = normalizedDigits.slice(9, 11);

    let formatted = `+7`;
    if (part1) formatted += ` (${part1}`;
    if (part2) formatted += `) ${part2}`;
    if (part3) formatted += `-${part3}`;
    if (part4) formatted += `-${part4}`;

    return formatted;
  };

    // ✅ Нормализация телефона перед отправкой на сервер
    const normalizePhone = (input: string): { isValid: boolean; normalized?: string; error?: string } => {
      const digits = input.replace(/\D/g, '');

      if (!digits) return { isValid: false, error: 'Введите номер телефона' };

      let cleaned: string;

      if (digits.startsWith('8') && digits.length === 11) {
        cleaned = digits.slice(1);
      } else if (digits.startsWith('7') && digits.length === 11) {
        cleaned = digits.slice(1);
      } else if (digits.length === 10) {
        cleaned = digits;
      } else {
        return { isValid: false, error: 'Введите корректный номер (10 цифр)' };
      }

      if (cleaned.length !== 10) {
        return { isValid: false, error: 'Номер должен содержать 10 цифр после +7' };
      }

      return { isValid: true, normalized: '+7' + cleaned };
    };

    // ✅ Обработчик ввода телефона с автоформатированием
    const handlePhoneChange = (
      e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      const formatted = formatPhone(newValue || '');
      setFormData(prev => ({ ...prev, phone: formatted }));
      setError('');
    };

  const handleInputChange = (field: keyof typeof formData) => (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setFormData(prev => ({ ...prev, [field]: newValue || '' }));
  };

  const handleRoleChange = (e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setFormData(prev => ({ ...prev, role: option.key as string }));
    }
  };

  const handleSubmit = () => {
    setError('');
    
    // 🔹 Валидация телефона
    if (!formData.phone || !formData.phone.trim()) {
      setError('Телефон обязателен');
      return;
    }

    // ✅ Нормализуем телефон перед отправкой
    const { isValid, normalized, error } = normalizePhone(formData.phone);
    if (!isValid) {
      setError(error || 'Некорректный номер телефона');
      return;
    }

    // ✅ Отправляем нормализованный телефон (+7XXXXXXXXXX)
    createUser({ 
      variables: { 
        ...formData, 
        phone: normalized! 
      } 
    });
  };

  const roleOptions: IDropdownOption[] = [
    { key: 'user', text: 'Пользователь' },
    ...(currentUserRole === 'superadmin' 
      ? []//{ key: 'admin', text: 'Администратор' }] 
      : [])
  ];

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      styles={{
        main: {
          width: 500,
          maxWidth: '100%',
          padding: 20,
          borderRadius: 8
        }
      }}
    >
      <h2>Создать нового пользователя</h2>
      
      {error && (
        <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 15 } }}>
          {error}
        </MessageBar>
      )}
      
      {success && (
        <MessageBar messageBarType={MessageBarType.success} styles={{ root: { marginBottom: 15 } }}>
          Пользователь успешно создан!
        </MessageBar>
      )}

      <Stack tokens={{ childrenGap: 15 }}>
        <TextField
          label="Имя пользователя"
          value={formData.username}
          onChange={handleInputChange('username')}
          required
        />
        
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleInputChange('email')}
          required
        />

        <TextField
          label="Телефон"
          value={formData.phone}
          onChange={handlePhoneChange}  // ✅ НОВЫЙ ОБРАБОТЧИК
          required
          placeholder="+7 (999) 999-99-99"
          description="Формат: +7 (999) 999-99-99"
        />
        
        <TextField
          label="Пароль"
          type="password"
          value={formData.password}
          onChange={handleInputChange('password')}
          required
          canRevealPassword
        />
        
        <TextField
          label="Компания"
          value={formData.company}
          onChange={handleInputChange('company')}
        />
        
        <Dropdown
          label="Роль"
          selectedKey={formData.role}
          onChange={handleRoleChange}
          options={roleOptions}
          disabled={currentUserRole !== 'superadmin'}
        />
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 20 } }}>
        <PrimaryButton onClick={handleSubmit}>Создать</PrimaryButton>
        <DefaultButton onClick={onDismiss}>Отмена</DefaultButton>
      </Stack>
    </Modal>
  );
};

export default CreateUserModal;